import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { MuniV3Schema, BuysideV1Schema } from './deal.service.types';
import { DealVersion, Deal } from '../types/deal.types';
import { PlatformType } from '../types/muni-environment';

type DealManagerResponse =
  | { issues: MuniV3Schema.Deal[] }
  | BuysideV1Schema.Deal[];

interface PlatformOptions {
  mapper: (DealManagerResponse) => Deal[];
  apiRoute: string;
}

@Injectable()
export class ManagerService {
  private readonly apiHost: string;
  private readonly platform: PlatformType;
  // TODO: Refactor when MUNI API v2 is ready
  private readonly platforms: { [key: string]: PlatformOptions } = {
    polaris: {
      mapper: ({ issues }): Deal[] => issues.map(MuniV3Schema.toManagerDeal),
      apiRoute: '/api/v1/deal-manager'
    },
    buyside: {
      mapper: (data: BuysideV1Schema.Deal[]): Deal[] =>
        data.map(BuysideV1Schema.toDeal),
      apiRoute: '/v2/deals/fixed-income/municipal/deal-monitor'
    }
  };

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    // TODO: delete "|| 'polaris'" after "@ipreo/northstar0.10.13" is deployed
    this.platform = (config.params.platform || 'polaris') as PlatformType;
    this.apiHost = config.params.apiHost as string;
  }

  public getDeals(): Observable<DealVersion[] | Error> {
    const url = `${this.apiHost}${this.platforms[this.platform].apiRoute}`;
    const platformOptions = this.platforms[this.platform];

    return this.http
      .get<DealManagerResponse>(url, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<DealManagerResponse>) => {
          const data = response.status === 200 && response.body;
          if (data) {
            return of(
              platformOptions.mapper(data).map((deal: Deal) => deal.published)
            );
          } else {
            return throwError(Error('No deals'));
          }
        })
      );
  }

  public getDealShowTypeDeal(dealId: string): Observable<Deal> {
    const url = `${this.apiHost}/api/v3/issues/${dealId}`;
    return this.http
      .get(url, { observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          const deal = response.status === 200 && response.body;
          return deal
            ? of(MuniV3Schema.toDeal(deal))
            : throwError(Error('No such issue by ' + dealId));
        })
      );
  }
}
